import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "../../../../store/registrationSlice";
import { toast } from "react-hot-toast";
import User from "../../../../api/auth";

const UserInformation = ({ nextStep }) => {
  const dispatch = useDispatch();
  const savedUserInfo = useSelector((state) => state.registration.userInfo);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load saved data if exists
    if (savedUserInfo) {
      setFormData(savedUserInfo);
    }
  }, [savedUserInfo]);

  const validateForm = () => {
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    // Phone validation
    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (phoneDigits.length !== 10) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      // Only allow digits and format them
      const formattedValue = formatPhoneNumber(value);
      if (formattedValue.replace(/\D/g, "").length <= 10) {
        setFormData((prev) => ({
          ...prev,
          [name]: formattedValue,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        // Check if email and phone are unique
        const response = await User.VerifyUnique({
          email: formData.email,
          phone: formData.phone,
        });

        dispatch(setUserInfo(formData));
        nextStep();
      } catch (error) {
        console.error("Verification error:", error);
        if (error.response?.data?.errors) {
          // Set field-specific errors from API response
          const apiErrors = {};
          Object.keys(error.response.data.errors).forEach(field => {
            apiErrors[field] = error.response.data.errors[field][0];
          });
          setErrors(apiErrors);
        } else {
          toast.error(
            error.response?.data?.message || "Error verifying information"
          );
        }
      }
    } else {
      toast.error("Please fill all required fields correctly");
    }
  };

  return (
    <div className="bg-white p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        User Information
      </h2>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.firstName
                  ? "border-red-500"
                  : "border-gray-300 focus:border-primary-500"
              } focus:outline-none focus:ring-1 focus:ring-primary-500`}
              placeholder="Enter first name"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.lastName
                  ? "border-red-500"
                  : "border-gray-300 focus:border-primary-500"
              } focus:outline-none focus:ring-1 focus:ring-primary-500`}
              placeholder="Enter last name"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.email
                ? "border-red-500"
                : "border-gray-300 focus:border-primary-500"
            } focus:outline-none focus:ring-1 focus:ring-primary-500`}
            placeholder="Enter email address"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.phone
                ? "border-red-500"
                : "border-gray-300 focus:border-primary-500"
            } focus:outline-none focus:ring-1 focus:ring-primary-500`}
            placeholder="Enter 10-digit number (e.g., 123-456-7890)"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="text"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.password
                ? "border-red-500"
                : formData.password.length >= 8
                ? "border-green-500"
                : "border-gray-300 focus:border-primary-500"
            } focus:outline-none focus:ring-1 focus:ring-primary-500`}
            placeholder="Enter password (minimum 8 characters)"
          />
          <div className="mt-1">
            {errors.password ? (
              <p className="text-sm text-red-500">{errors.password}</p>
            ) : (
              <p
                className={`text-sm ${
                  formData.password.length >= 8
                    ? "text-green-600"
                    : "text-gray-500"
                }`}
              >
                {formData.password.length}/8 characters
              </p>
            )}
          </div>
        </div>
        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSubmit}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserInformation;
